import { PERMISSIONS } from '@conversed/shared/src/central/constants/permissions';
import { z } from 'zod';
import { createRESTContract } from '../../index';
import {
  ArticleArchitectBccStatusSchema,
  ArticleArchitectUploadRecordSchema,
  ArticleListPaginationQuerySchema,
  ArticleSourceTicketsResponseSchema,
  BackfillRunStatusResponseSchema,
  ConflictGroupSchema,
  KnowledgeArticleSchema,
  KnowledgeArticleStatsSchema,
  ResolveConflictBodySchema,
  SourceTicketsBackfillResponseSchema,
  TicketIncrementalCountResponseSchema,
  TicketIngestionStatusQuerySchema,
  TicketIngestionStatusResponseSchema,
  UpdateKnowledgeArticleBodySchema,
} from './article-architect.schema';

export const articleArchitectContract = createRESTContract({
  getUploads: {
    path: '/article-architect/uploads',
    method: 'GET',
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_VIEW],
    body: z.undefined(),
    response: z.array(ArticleArchitectUploadRecordSchema),
  },
  getArticles: {
    path: '/article-architect/articles',
    method: 'GET',
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_VIEW],
    body: z.undefined(),
    query: ArticleListPaginationQuerySchema,
    response: z.array(KnowledgeArticleSchema),
  },
  getArticleSourceTickets: {
    path: '/article-architect/articles/:articleId/source-tickets',
    method: 'GET',
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_VIEW],
    body: z.undefined(),
    response: ArticleSourceTicketsResponseSchema,
  },
  getArticleStats: {
    path: '/article-architect/articles/stats',
    method: 'GET',
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_VIEW],
    body: z.undefined(),
    response: KnowledgeArticleStatsSchema,
  },
  getBccStatus: {
    path: '/article-architect/bcc-status',
    method: 'GET',
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_VIEW],
    body: z.undefined(),
    response: ArticleArchitectBccStatusSchema,
  },
  triggerBccBatch: {
    path: '/article-architect/bcc-trigger',
    method: 'POST',
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_GENERATE],
    body: z.undefined(),
    response: z.object({ message: z.string() }),
  },
  updateArticle: {
    path: '/article-architect/articles/:articleId',
    method: 'PATCH',
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_EDIT],
    body: UpdateKnowledgeArticleBodySchema,
    response: KnowledgeArticleSchema,
  },
  deleteArticle: {
    path: '/article-architect/articles/:articleId',
    method: 'DELETE',
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_DELETE],
    body: z.undefined(),
    response: z.void(),
  },
  getConflicts: {
    path: '/article-architect/conflicts',
    method: 'GET',
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_VIEW],
    body: z.undefined(),
    query: ArticleListPaginationQuerySchema,
    response: z.array(ConflictGroupSchema),
  },
  resolveConflict: {
    path: '/article-architect/articles/:articleId/resolve-conflict',
    method: 'POST',
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_GENERATE],
    body: ResolveConflictBodySchema,
    response: ConflictGroupSchema,
  },
  startConflictScan: {
    path: '/article-architect/conflict-scan',
    method: 'POST',
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_GENERATE],
    body: z.undefined(),
    response: z.object({ message: z.string() }),
  },
  getTicketIngestionStatus: {
    path: '/article-architect/ticket-ingestion-status',
    method: 'GET',
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_VIEW],
    query: TicketIngestionStatusQuerySchema,
    response: TicketIngestionStatusResponseSchema,
  },
  getTicketIncrementalCount: {
    path: '/article-architect/ticket-incremental-count',
    method: 'GET',
    superAdminOnly: true,
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_VIEW],
    query: TicketIngestionStatusQuerySchema,
    response: TicketIncrementalCountResponseSchema,
  },
  getBackfillRunStatus: {
    path: '/article-architect/backfill-run-status',
    method: 'GET',
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_VIEW],
    query: TicketIngestionStatusQuerySchema,
    response: BackfillRunStatusResponseSchema,
  },
  // * No frontend calls this route; a super admin starts it by hand.
  backfillSourceTickets: {
    path: '/article-architect/topdesk/source-tickets/backfill',
    method: 'POST',
    superAdminOnly: true,
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_GENERATE],
    body: z.undefined(),
    response: SourceTicketsBackfillResponseSchema,
  },
  syncBlockKnowledgeArticles: {
    path: '/article-architect/blocks/:blockId/sync-knowledge-articles',
    method: 'POST',
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_EDIT],
    body: z.undefined(),
    response: z.object({ message: z.string(), keys: z.array(z.string()) }),
  },
  clearBlockKnowledgeArticles: {
    path: '/article-architect/blocks/:blockId/clear-knowledge-articles',
    method: 'POST',
    permissions: [PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_EDIT],
    body: z.undefined(),
    response: z.object({ message: z.string() }),
  },
});
